import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import {
  AccountStates,
  AffiliationRelations,
  AffiliationTypes,
  EntityTypes,
  FeatureFlagTypes,
  GoalTypes,
} from '@generated/graphql';
import type { Account } from '@interfaces/account';
import { getAccountTypes } from '@interfaces/account';
import type { TranslatedString } from '@interfaces/shared';
import { AccountTypes, Jurisdictions } from '@onevesthq/ov-enums';
import { useConfirmationDialog } from '@ovComponents/4-module/workflowCompletion/components/useConfirmationDialog';
import { useRespTransferSync } from '@ovComponents/4-module/workflowCompletion/subSteps/respTransfer/useRespTransferSync';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { UserContext } from '@providers/userContextProvider';
import { FlagsmithFlags } from '@utils/flagsmith';
import { useToast } from 'hephaestus/web';
import { difference, first, intersection, uniq } from 'lodash-es';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useOrganizationFeatureFlag } from '../../../../../hooks/useOrganizationFeatureFlag';
import { FETCH_ACCOUNT_SUBACCOUNTS } from './createAccountForGoal.queries';
import { CreateAccountForGoalVisual } from './createAccountForGoal.visual';
import { useAccountCreationMethodChoice } from './useAccountCreationMethodChoice';
import { useRepCodeSelection } from './useRepCodeSelection';

const HARDCODED_GOAL_WORKFLOW_ID = '123';
const HARCODED_GOAL_WORFLOW_EN_NAME = 'New Goal';

export type CreateAccountForGoalArgs = {
  accountType: AccountTypes;
  isTransferredFromAnotherInstitution?: boolean;
};

const CREATE_SUB_ACCOUNT = gql`
  mutation createSubAccount($input: CreateSubAccountInput!) {
    createSubAccount(input: $input) {
      subAccount {
        id
        account {
          id
          state
          type
          affiliations {
            id
            relation
            type
          }
          user {
            id
            primaryEmail
            type
          }
        }
      }
    }
  }
`;

const CREATE_ACCOUNT = gql`
  mutation createAccount($input: CreateAccountInput!) {
    createAccount(input: $input) {
      account {
        id
        type
      }
    }
  }
`;

const UPDATE_ACCOUNT_AFFILIATIONS = gql`
  mutation updateAffiliations($input: UpdateAffiliationsInput!) {
    updateAffiliations(input: $input) {
      account {
        id
      }
      incompleteAffiliations {
        id
        relation
        type
        incompleteFields
      }
    }
  }
`;

const FETCH_USER = gql`
  query fetchUser($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        dateOfBirth
        type
        accounts {
          id: stringId
          type
          state
          isTransferredFromAnotherInstitution
          translatedNickName { ${TRANSLATED_STRING_FIELDS} }
          affiliations { id relation type user { id dateOfBirth } }
          subAccounts {
            id
          }
          householdClientGroup { id }
        }
        physicalAddress { jurisdiction }
      }
    }
  }
`;

const FETCH_HOUSEHOLD = gql`
  query fetchHousehold($clientGroupId: SafeString!) {
    fetchClientGroup(input: { clientGroupId: $clientGroupId }) {
      clientGroup {
        primaryUser {
          id: stringId
          dateOfBirth
          type
          physicalAddress { jurisdiction }
        }
        accounts {
          id: stringId
          type
          state
          isTransferredFromAnotherInstitution
          translatedNickName { ${TRANSLATED_STRING_FIELDS} }
          affiliations { id relation type user { id dateOfBirth } }
          subAccounts {
            id
          }
        }
      }
    }
  }
`;

const CLOSE_ACCOUNT = gql`
  mutation closeAccount($input: CloseAccountInput!) {
    closeAccount(input: $input) {
      account {
        id
      }
    }
  }
`;

const CLOSE_SUB_ACCOUNT = gql`
  mutation closeSubAccount($input: CloseSubAccountInput!) {
    closeSubAccount(input: $input) {
      subAccount {
        id
      }
    }
  }
`;

// Suggested account types per goal type to be shown first
const GOAL_ACCOUNT_MAP: { [key in GoalTypes]: AccountTypes[] } = {
  [GoalTypes.Education]: [
    AccountTypes.RESP_FAMILY,
    AccountTypes.RESP_FAMILY_JOINT,
    AccountTypes.USA_529,
    AccountTypes.USA_ESA,
  ],
  [GoalTypes.MajorPurchase]: [
    AccountTypes.TFSA,
    AccountTypes.PERSONAL,
    AccountTypes.FHSA,
    AccountTypes.CASH_JOINT,
    AccountTypes.USA_BROKERAGE,
    AccountTypes.USA_JT_CP,
    AccountTypes.USA_JT_JTBE,
    AccountTypes.USA_JT_TIC,
    AccountTypes.USA_JT_WROS,
  ],
  [GoalTypes.Retirement]: [
    AccountTypes.RRSP,
    AccountTypes.RRSP_SPOUSAL,
    AccountTypes.USA_IRA,
    AccountTypes.USA_RT_IRA,
    AccountTypes.USA_INH_IRA,
    AccountTypes.USA_INH_RT_IRA,
    AccountTypes.USA_SEP_IRA,
    AccountTypes.USA_SOLO_401K,
  ],
  [GoalTypes.Car]: [],
  [GoalTypes.BuildWealth]: [
    AccountTypes.USA_BROKERAGE,
    AccountTypes.USA_JT_CP,
    AccountTypes.USA_JT_JTBE,
    AccountTypes.USA_JT_TIC,
    AccountTypes.USA_JT_WROS,
    AccountTypes.USA_IRA,
    AccountTypes.USA_RT_IRA,
  ],
  [GoalTypes.House]: [],
  [GoalTypes.Vacation]: [],
  [GoalTypes.StartABusiness]: [],
  [GoalTypes.Wedding]: [],
  [GoalTypes.ParentalLeave]: [],
  [GoalTypes.SafetyNet]: [],
  [GoalTypes.Other]: [],
  [GoalTypes.CashReserve]: [
    AccountTypes.TFSA,
    AccountTypes.PERSONAL,
    AccountTypes.CASH_JOINT,
  ],
  [GoalTypes.Legacy]: [AccountTypes.PERSONAL, AccountTypes.CASH_JOINT],
  [GoalTypes.Group]: [],
  [GoalTypes.RetirementIncome]: [AccountTypes.RRSP, AccountTypes.RRSP_SPOUSAL],
};

const ACCOUNT_JURISDICTION_ALLOW_LIST: {
  [key in AccountTypes]?: Jurisdictions[];
} = {
  [AccountTypes.USA_JT_JTBE]: [
    Jurisdictions.US_AK,
    Jurisdictions.US_AR,
    Jurisdictions.US_DE,
    Jurisdictions.US_FL,
    Jurisdictions.US_HI,
    Jurisdictions.US_IL,
    Jurisdictions.US_IN,
    Jurisdictions.US_KY,
    Jurisdictions.US_MD,
    Jurisdictions.US_MA,
    Jurisdictions.US_MI,
    Jurisdictions.US_MS,
    Jurisdictions.US_MO,
    Jurisdictions.US_NJ,
    Jurisdictions.US_NY,
    Jurisdictions.US_NC,
    Jurisdictions.US_OH,
    Jurisdictions.US_OK,
    Jurisdictions.US_OR,
    Jurisdictions.US_PA,
    Jurisdictions.US_RI,
    Jurisdictions.US_TN,
    Jurisdictions.US_VT,
    Jurisdictions.US_VA,
    Jurisdictions.US_WY,
  ],
  [AccountTypes.USA_JT_CP]: [
    Jurisdictions.US_AZ,
    Jurisdictions.US_CA,
    Jurisdictions.US_ID,
    Jurisdictions.US_LA,
    Jurisdictions.US_NM,
    Jurisdictions.US_NV,
    Jurisdictions.US_TX,
    Jurisdictions.US_WA,
    Jurisdictions.US_WI,
  ],
};

const isUserEighteenBeforeBeginingOfTheYear = (
  dateOfBirthString: string,
): boolean => {
  const date = new Date(dateOfBirthString);
  const ageUserWillBeThisYear = new Date().getFullYear() - date.getFullYear();
  const ageLastYear = ageUserWillBeThisYear - 1;
  return ageLastYear >= 18;
};

export type CreateAccountForGoalOptions = {
  title: TranslatedString;
  subtitle: TranslatedString;
  commonTypesLabel: TranslatedString;
  accountOpeningMethod: 'NEW_ACCOUNT' | 'NEW_SUB_ACCOUNT' | 'USER_CHOICE';
  autoAssignAuthorizedIndividual: boolean;
  autoAssignBeneficialOwner: boolean;
  autoAssignIndividualAsDirector: boolean;
  displayAccountDefinitions: boolean;
};

export const CreateAccountForGoal = ({
  options: propsOptions,
  objectId,
  userId,
  onNext,
  stepLoading,
  workflowCompletion,
  objectType = 'USER',
}: {
  options: CreateAccountForGoalOptions;
  objectId: string;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: any;
  objectType?: string;
}) => {
  const options = {
    ...propsOptions,
    accountOpeningMethod:
      propsOptions.accountOpeningMethod || 'NEW_SUB_ACCOUNT',
  };
  const { t } = useTranslation('workflowCompletions');
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const { activeOrganization, userContext, organizationFeatureFlags } =
    useContext(UserContext);
  const { enqueueToast } = useToast();
  const hasCreateAccountOrSubAccountConfig = useOrganizationFeatureFlag(
    FeatureFlagTypes.CreateAccountOrSubAccountConfig,
  );
  const isRespInstitutionTransferEnabled = useFeatureFlag(
    FlagsmithFlags.resp_transferred_from_another_institution,
  );

  const [suggestedTypes, setSuggestedTypes] = useState<AccountTypes[]>([]);
  const [otherTypes, setOtherTypes] = useState<AccountTypes[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const { repCode, validateRepCode, repCodeField } = useRepCodeSelection();

  const [updateAccountAffiliations] = useMutation(UPDATE_ACCOUNT_AFFILIATIONS);
  const { syncRespTransferSelection } = useRespTransferSync();
  const [closeAccount] = useMutation(CLOSE_ACCOUNT);
  const [closeSubAccount] = useMutation(CLOSE_SUB_ACCOUNT);

  // match the id and name of the hardcoded subaccount workflow
  // i18n: Using .en to match hardcoded workflow name - fragile pattern, workflow ID + name must match
  const isGoalHardcodedWorkflow =
    workflowCompletion?.id === HARDCODED_GOAL_WORKFLOW_ID &&
    workflowCompletion?.workflow?.steps?.[0]?.name?.en ===
      HARCODED_GOAL_WORFLOW_EN_NAME;

  const {
    data: userData,
    loading: userLoading,
    refetch: refetchUserData,
  } = useQuery(objectType === 'HOUSEHOLD' ? FETCH_HOUSEHOLD : FETCH_USER, {
    variables: {
      ...(objectType === 'HOUSEHOLD'
        ? { clientGroupId: objectId }
        : { userId }),
    },
    fetchPolicy: 'no-cache',
    onCompleted: (result) => {
      const user =
        objectType === 'HOUSEHOLD'
          ? result.fetchClientGroup.clientGroup.primaryUser
          : result.fetchUser.user;
      const userType = user.type;
      const userJurisdiction = user.physicalAddress
        ?.jurisdiction as Jurisdictions;
      determineAccountTypes(userType, userJurisdiction);
    },
  });

  // Lazy query to fetch fresh subaccount data for a specific account
  const [fetchAccountSubAccounts] = useLazyQuery(FETCH_ACCOUNT_SUBACCOUNTS, {
    fetchPolicy: 'network-only',
  });

  const existingAccounts: Account[] =
    objectType === 'HOUSEHOLD'
      ? userData?.fetchClientGroup?.clientGroup?.accounts || []
      : workflowData.currentHouseholdClientGroupId
        ? (userData?.fetchUser?.user?.accounts || []).filter(
            (account: Account) =>
              account.householdClientGroup?.id ===
              workflowData.currentHouseholdClientGroupId,
          )
        : userData?.fetchUser?.user?.accounts || [];
  const activeAccounts = existingAccounts.filter((x) =>
    [
      AccountStates.Active,
      AccountStates.Frozen,
      AccountStates.Initiated,
      AccountStates.Ready,
      AccountStates.Requested,
    ].includes(x.state),
  );

  const userDOB: string =
    objectType === 'HOUSEHOLD'
      ? userData?.fetchClientGroup?.clientGroup?.primaryUser?.dateOfBirth
      : userData?.fetchUser?.user?.dateOfBirth || '';
  const activeAccountsByType = activeAccounts.reduce(
    (acc, account: Account) => {
      acc[account.type] = [...(acc[account.type] || []), account];
      return acc;
    },
    {} as Partial<Record<AccountTypes, Account[]>>,
  );

  const determineAccountTypes = async (
    userType: EntityTypes,
    userJurisdiction: Jurisdictions,
  ) => {
    // types allowed by feature flags, by country
    const availableTypes0 = getAccountTypes(
      userType,
      Array.from(organizationFeatureFlags),
      activeOrganization.applicableLocalization?.countries,
    );

    // drop types out of client's jurisdiction
    const availableTypes = availableTypes0.filter((type) => {
      if (!userJurisdiction) return true;
      const allowList = ACCOUNT_JURISDICTION_ALLOW_LIST[type];
      if (!allowList) return true;
      if (allowList.includes(userJurisdiction)) return true;
      return false;
    });

    // out of availableTypes, pick some as "suggested"
    const allSuggestedTypes =
      GOAL_ACCOUNT_MAP[workflowData.currentGoalType as GoalTypes] || [];
    setSuggestedTypes(intersection(allSuggestedTypes, availableTypes) || []);
    setOtherTypes(difference(availableTypes, allSuggestedTypes));
  };

  const autoAssignAffiliations = (account: Account) => {
    if (account.affiliations && account.affiliations.length > 0) {
      return;
    }
    const contextIndividuals =
      (userContext?.entities ?? []).filter(
        (e) =>
          e.entity.type === EntityTypes.Individual && !e.entity.affiliateOnly,
      ) ?? [];
    if (
      uniq(contextIndividuals.map((entityObj: any) => entityObj?.entity?.id))
        .length !== 1
    ) {
      /* TODO: should be contextIndividuals.length !== 1, but currently we are experiencing a known
        bug which is entities getting duplicated after newly added
      */
      return;
    }
    const individual = first(contextIndividuals)?.entity;
    const affiliations: any[] = [];
    if (individual) {
      if (options.autoAssignAuthorizedIndividual) {
        affiliations.push({
          relation: AffiliationRelations.Other,
          type: AffiliationTypes.AuthorizedIndividual,
          userId: individual.id,
        });
      }
      if (options.autoAssignIndividualAsDirector) {
        affiliations.push({
          relation: AffiliationRelations.Other,
          type: AffiliationTypes.Director,
          userId: individual.id,
        });
      }
      if (options.autoAssignBeneficialOwner) {
        affiliations.push({
          relation: AffiliationRelations.Other,
          type: AffiliationTypes.BeneficialOwner,
          userId: individual.id,
          allocation: 100,
        });
      }
      if (account.type === AccountTypes.RESP_ADULT) {
        affiliations.push({
          relation: AffiliationRelations.Other,
          type: AffiliationTypes.PrimaryBeneficiary,
          userId: individual.id,
          allocation: 100,
        });
      }
    }
    if (affiliations.length === 0) {
      return;
    }
    updateAccountAffiliations({
      variables: {
        input: { accountId: account.id, affiliations },
      },
      onCompleted: (data: any) => {
        // if entity is incomplete thre will be incompleteAffiliations and incompleteFields and affiliations will not be added
        if (
          data?.updateAffiliations?.incompleteAffiliations &&
          data?.updateAffiliations?.incompleteAffiliations?.length > 0
        ) {
          enqueueToast(
            t('corporateCashAccountCreation.incompleteAffiliationError'),
            { variant: 'error' },
          );
        }
      },
    });
  };

  const [createAccountMutation] = useMutation(CREATE_ACCOUNT);
  const executeCreateAccountFlow = async (
    accountType: string,
    isTransferredFromAnotherInstitution = false,
  ) => {
    const { data } = await createAccountMutation({
      variables: {
        input: {
          userId:
            objectType === 'HOUSEHOLD'
              ? userData?.fetchClientGroup?.clientGroup?.primaryUser?.id
              : userId,
          type: accountType,
          applyForGovFunds: accountType.includes('RESP')
            ? ['BASIC_CESG']
            : undefined,
          isTransferredFromAnotherInstitution:
            isRespInstitutionTransferEnabled && accountType.includes('RESP')
              ? isTransferredFromAnotherInstitution
              : undefined,
          householdClientGroupId:
            objectType === 'HOUSEHOLD'
              ? objectId
              : (workflowData.currentHouseholdClientGroupId ?? undefined),
          ...(repCode?.code && { repCode: repCode.code }),
        },
      },
    });

    if (!data?.createAccount) {
      enqueueToast(t('createAccountForGoal.createAccountError'), {
        variant: 'error',
      });
      return;
    }

    await setWorkflowData((prev) => ({
      ...prev,
      currentAccountId: data?.createAccount.account.id,
      currentAccountType: accountType,
      currentAccountState: AccountStates.Initiated,
      createdAccounts: [
        ...(prev.createdAccounts || []),
        data?.createAccount.account,
      ],
      skipEditAccount: false,
    }));
    await executeCreateSubAccountFlow(
      data?.createAccount.account?.id,
      accountType,
      false,
    );
  };

  const [createSubAccountMutation] = useMutation(CREATE_SUB_ACCOUNT);
  const executeCreateSubAccountFlow = async (
    accountId: string,
    accountType: string,
    skipEditAccountForHardcodedWorkflow = true,
  ) => {
    const { data: subAccountData } = await createSubAccountMutation({
      variables: {
        input: {
          accountId,
          accountType,
          goalId: workflowData.currentGoalId,
          userId:
            objectType === 'HOUSEHOLD'
              ? userData?.fetchClientGroup?.clientGroup?.primaryUser?.id
              : userId,
        },
      },
    });

    if (!subAccountData?.createSubAccount) {
      enqueueToast(t('createAccountForGoal.createSubAccountError'), {
        variant: 'error',
      });
      return;
    }

    if (
      accountType === AccountTypes.CORPORATE_CASH &&
      (options.autoAssignAuthorizedIndividual ||
        options.autoAssignBeneficialOwner ||
        options.autoAssignIndividualAsDirector)
    ) {
      const account = subAccountData?.createSubAccount?.subAccount?.account;
      autoAssignAffiliations(account);
    }
    await setWorkflowData((prev) => ({
      ...prev,
      currentAccountId:
        subAccountData?.createSubAccount?.subAccount?.account?.id,
      currentAccountType: accountType,
      currentSubAccountId: subAccountData?.createSubAccount?.subAccount?.id,
      createdSubAccountIds: [
        ...(prev.createdSubAccountIds || []),
        subAccountData?.createSubAccount?.subAccount?.id,
      ],
      skipEditAccount:
        isGoalHardcodedWorkflow && skipEditAccountForHardcodedWorkflow,
    }));
  };

  const rollback = async () => {
    if (
      workflowData.currentAccountId &&
      workflowData.currentAccountState === AccountStates.Initiated
    ) {
      await closeAccount({
        variables: {
          input: {
            accountId: workflowData.currentAccountId,
            inactiveReason: 'OTHER',
          },
        },
      });
    }
    if (workflowData.currentSubAccountId) {
      await closeSubAccount({
        variables: {
          input: {
            subAccountId: workflowData.currentSubAccountId,
            inactiveReason: 'OTHER',
          },
        },
      });
    }
    await setWorkflowData((prev) => ({
      ...prev,
      currentAccountId: undefined,
      currentAccountType: undefined,
      currentSubAccountId: undefined,
      currentAccountState: undefined,
      createdAccounts: prev.createdAccounts?.filter(
        (account: any) => account.id !== prev.currentAccountId,
      ),
      createdSubAccountIds: prev.createdSubAccountIds?.filter(
        (subAccount: any) => subAccount !== prev.currentSubAccountId,
      ),
    }));
  };

  const { getUserConfirmation, confirmationDialog } = useConfirmationDialog();
  const { getAccountCreationMethodChoice, accountCreationMethodChoiceDialog } =
    useAccountCreationMethodChoice();
  const onContinue = async ({
    accountType,
    isTransferredFromAnotherInstitution = false,
  }: CreateAccountForGoalArgs) => {
    if (
      accountType === 'RESP_ADULT' &&
      !isUserEighteenBeforeBeginingOfTheYear(userDOB)
    ) {
      enqueueToast(t('respAccountCreation.respAgeRestrictionError'), {
        variant: 'error',
      });
      return;
    }

    if (!validateRepCode()) return;
    setSaving(true);

    if (!hasCreateAccountOrSubAccountConfig) {
      const account = activeAccountsByType[accountType]?.at(0);
      if (account) {
        // Refetch after a real write so the no-cache account snapshot isn't stale.
        if (
          await syncRespTransferSelection(
            account,
            isTransferredFromAnotherInstitution,
          )
        ) {
          await refetchUserData();
        }
        await executeCreateSubAccountFlow(account.id, accountType);
      } else {
        await executeCreateAccountFlow(
          accountType,
          isTransferredFromAnotherInstitution,
        );
      }
      await onNext();
      setSaving(false);
      return;
    }

    const accountTypeDisplay = t(`accountTypes:${accountType}`);

    switch (true) {
      case !activeAccountsByType[accountType]?.length: {
        await rollback();
        await executeCreateAccountFlow(
          accountType,
          isTransferredFromAnotherInstitution,
        );
        break;
      }

      case accountType === workflowData.currentAccountType: {
        const reusedAccount =
          activeAccountsByType[accountType]?.find(
            (item) => item.id === workflowData.currentAccountId,
          ) ?? activeAccountsByType[accountType]?.at(0);
        // Refetch after a real write so the no-cache account snapshot isn't stale.
        if (
          reusedAccount &&
          (await syncRespTransferSelection(
            reusedAccount,
            isTransferredFromAnotherInstitution,
          ))
        ) {
          await refetchUserData();
        }
        // If currentSubAccountId is missing but we have the account, try to recover
        if (
          !workflowData.currentSubAccountId &&
          workflowData.currentAccountId &&
          workflowData.currentGoalId
        ) {
          // Fetch fresh subaccount data for the specific account
          const { data: accountData } = await fetchAccountSubAccounts({
            variables: { accountId: workflowData.currentAccountId },
          });

          const existingSubAccount =
            accountData?.fetchAccount?.account?.subAccounts?.find(
              (subAccount) =>
                subAccount.goal?.id === workflowData.currentGoalId,
            );

          if (existingSubAccount) {
            // Reuse the existing subaccount instead of creating a new one
            await setWorkflowData((prev) => ({
              ...prev,
              currentSubAccountId: existingSubAccount.id,
            }));
          } else {
            // No existing subaccount found, create a new one
            await executeCreateSubAccountFlow(
              workflowData.currentAccountId,
              accountType,
            );
            enqueueToast(t('createAccountForGoal.duplicateSubAccountWarning'), {
              variant: 'warning',
            });
          }
        }
        break;
      }

      case options.accountOpeningMethod === 'NEW_ACCOUNT': {
        const userConfirmation = await getUserConfirmation({
          title: t('createAccountForGoal.confirmDialog.createNewAccount.title'),
          bodyText: t(
            'createAccountForGoal.confirmDialog.createNewAccount.body',
            { accountType: accountTypeDisplay },
          ),
          autoConfirmKey: 'NEW_ACCOUNT',
        });
        if (!userConfirmation.confirmed) {
          setSaving(false);
          userConfirmation.closeDialog();
          return;
        }
        await rollback();
        await executeCreateAccountFlow(
          accountType,
          isTransferredFromAnotherInstitution,
        );
        userConfirmation.closeDialog();
        break;
      }

      case options.accountOpeningMethod === 'USER_CHOICE' ||
        (activeAccountsByType[accountType]?.length || 0) > 1: {
        const userChoiceResult = await getAccountCreationMethodChoice({
          accountType,
          activeAccounts: activeAccountsByType[accountType] as Account[],
          allowNewAccount: options.accountOpeningMethod !== 'NEW_SUB_ACCOUNT',
          // Host the RESP transfer toggle in this dialog so it reflects the account the
          // user actually picks (the standalone tile dialog can't, since the account
          // isn't known yet when multiple of the same type exist).
          showRespTransferToggle:
            isRespInstitutionTransferEnabled && accountType.includes('RESP'),
        });
        const choice = userChoiceResult?.choice;
        if (!choice) {
          /** User cancelled the dialog */
          setSaving(false);
          userChoiceResult.closeDialog();
          return;
        }
        // Use the value captured against the chosen account, not the (ambiguous) tile
        // dialog value, to avoid clobbering an existing account's transfer flag.
        const respTransferSelection =
          userChoiceResult.isTransferredFromAnotherInstitution;
        await rollback();
        switch (choice.method) {
          case 'NEW_ACCOUNT': {
            await executeCreateAccountFlow(accountType, respTransferSelection);
            break;
          }
          case 'NEW_SUB_ACCOUNT': {
            // The RESP transfer flag lives on the account, so honor the dialog choice
            // by syncing it onto the existing account the sub-account is added to.
            // Refetch after a real write so the no-cache account snapshot isn't stale.
            if (
              await syncRespTransferSelection(
                choice.account,
                respTransferSelection,
              )
            ) {
              await refetchUserData();
            }
            await executeCreateSubAccountFlow(choice.account.id, accountType);
            break;
          }
        }
        userChoiceResult.closeDialog();
        break;
      }

      case options.accountOpeningMethod === 'NEW_SUB_ACCOUNT': {
        const userConfirmation = await getUserConfirmation({
          title: t(
            'createAccountForGoal.confirmDialog.createNewSubAccount.title',
          ),
          bodyText: t(
            'createAccountForGoal.confirmDialog.createNewSubAccount.body',
            { accountType: accountTypeDisplay },
          ),
          autoConfirmKey: 'NEW_SUB_ACCOUNT',
        });
        if (!userConfirmation.confirmed) {
          setSaving(false);
          userConfirmation.closeDialog();
          return;
        }
        await rollback();
        const reusedAccount = activeAccountsByType[accountType]?.at(0);
        // The RESP transfer flag lives on the account, so honor the dialog choice
        // by syncing it onto the existing account the sub-account is added to.
        // Refetch after a real write so the no-cache account snapshot isn't stale.
        if (
          reusedAccount &&
          (await syncRespTransferSelection(
            reusedAccount,
            isTransferredFromAnotherInstitution,
          ))
        ) {
          await refetchUserData();
        }
        await executeCreateSubAccountFlow(
          reusedAccount?.id as string,
          accountType,
        );
        userConfirmation.closeDialog();
        break;
      }
    }

    await onNext();
    setSaving(false);
  };

  /**
   * Returns the persisted transfer flag of the RESP account that would be reused for the
   * given type, so the dialog can pre-fill the toggle with the value already in the DB
   * instead of always defaulting to false. Mirrors the reuse logic in `onContinue`.
   */
  const getReusableRespTransferValue = (selectedType: string): boolean => {
    const accounts = activeAccountsByType[selectedType as AccountTypes];
    if (!accounts?.length) return false;
    let account: Account | undefined;
    if (!hasCreateAccountOrSubAccountConfig) {
      account = accounts.at(0);
    } else if (selectedType === workflowData.currentAccountType) {
      account =
        accounts.find((item) => item.id === workflowData.currentAccountId) ??
        accounts.at(0);
    } else if (
      options.accountOpeningMethod !== 'NEW_ACCOUNT' &&
      accounts.length === 1
    ) {
      // A single existing account of this type will be reused (auto for NEW_SUB_ACCOUNT,
      // or the only reuse option for USER_CHOICE), so pre-fill the toggle with its
      // persisted value. This keeps the sync on confirm a no-op unless the user actually
      // changes it, avoiding an accidental flip of the account's transfer flag.
      account = accounts.at(0);
    }
    return account?.isTransferredFromAnotherInstitution ?? false;
  };

  /**
   * Mirrors the routing in `onContinue`: returns true when selecting this type will open
   * the account-creation-method dialog, which now hosts the RESP transfer toggle. The
   * visual uses this to skip the standalone transfer dialog for that path, so the toggle
   * is captured against the account the user actually chooses (prevents clobbering when
   * multiple active accounts of the type exist).
   */
  const willPromptAccountCreationMethodChoice = (
    selectedType: string,
  ): boolean => {
    if (!hasCreateAccountOrSubAccountConfig) return false;
    const count =
      activeAccountsByType[selectedType as AccountTypes]?.length ?? 0;
    if (count === 0) return false;
    if (selectedType === workflowData.currentAccountType) return false;
    if (options.accountOpeningMethod === 'NEW_ACCOUNT') return false;
    return options.accountOpeningMethod === 'USER_CHOICE' || count > 1;
  };

  return (
    <CreateAccountForGoalVisual
      options={options}
      suggestedTypes={suggestedTypes}
      otherTypes={otherTypes}
      accountType={workflowData.currentAccountType ?? ''}
      activeAccountsByType={activeAccountsByType}
      getReusableRespTransferValue={getReusableRespTransferValue}
      willPromptAccountCreationMethodChoice={
        willPromptAccountCreationMethodChoice
      }
      continueFunc={onContinue}
      loading={userLoading || stepLoading}
      saving={saving}
      workflowCompletion={workflowCompletion}
      confirmationDialog={confirmationDialog}
      accountCreationMethodChoiceDialog={accountCreationMethodChoiceDialog}
      repCodeField={repCodeField}
      validateRepCode={validateRepCode}
    />
  );
};

export default CreateAccountForGoal;
